import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import API from "../../utils/api";
import LoaderComp from "../LoaderComp";
import ProductCard from "../ProductCard";
import ProductCardDetailSkeleton from "../ProductCardDetailSkeleton";
import ProductCardSkeleton from "../ProductCardSkeleton";
import SliderComp from "../SliderComp";
import classes from "./ProductDetail.module.css";
import ProductImageZoom from "../ProductImageZoom";
import { PLACEHOLDER_IMAGE } from "../../utils/image";
import GetAQuoteButton from "../GetAQuoteButton";
import PageSchema from "../PageSchema";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    // Check if we have server-rendered product data
    const serverDataElement = document.getElementById("server-product-data");
    if (serverDataElement) {
      try {
        const serverProductData = JSON.parse(serverDataElement.textContent);
        console.log("Using server-rendered product data");
        setProduct(serverProductData);
        setSelectedImage(0);
        if (serverProductData.category_slug) {
          fetchRelatedProducts(
            serverProductData.category_slug,
            serverProductData.id,
          );
        }
        setLoading(false);
        return; // Skip API call if we have server data
      } catch (error) {
        console.error("Error parsing server product data:", error);
      }
    }

    // Fallback to API call if no server data
    let mounted = true;
    async function loadProduct() {
      setLoading(true);
      try {
        const res = await fetch(
          `${API.base}/getProduct.php?slug=${encodeURIComponent(slug)}`,
        );
        const json = await res.json();
        if (mounted && res.ok && json.success) {
          setProduct(json.data);
          setSelectedImage(0);
          fetchRelatedProducts(json.data.category_slug, json.data.id);
        } else if (mounted) {
          setProduct(null);
          setRelatedProducts([]);
        }
      } catch (err) {
        if (mounted) {
          setProduct(null);
          setRelatedProducts([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadProduct();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const fetchRelatedProducts = async (categorySlug, excludeId) => {
    if (!categorySlug) return setRelatedProducts([]);
    setRelatedLoading(true);
    try {
      const params = new URLSearchParams({
        action: "fetch",
        category_slug: categorySlug,
        limit: "12",
        status: "publish",
      });
      if (excludeId) params.append("exclude_id", excludeId);
      const res = await fetch(`${API.base}/products.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.data || [];
      setRelatedProducts(list);
    } catch (err) {
      setRelatedProducts([]);
    } finally {
      setRelatedLoading(false);
    }
  };

  const getImageUrl = (img) => {
    const baseUrl = API.base.endsWith("/primecloudsystems_api")
      ? API.base
      : `${API.base}/primecloudsystems_api`;

    if (!img) {
      return `${baseUrl}/upload/placeholder-image.jpg`;
    }

    // Return actual image filename (backend already cleans missing files)
    return `${baseUrl}/upload/${img}`;
  };

  const galleryImages = useMemo(() => {
    if (!product) return [];
    let arr = [];
    if (product.productImage) arr.push(product.productImage);
    if (product.productGallery) {
      try {
        const gallery =
          typeof product.productGallery === "string"
            ? JSON.parse(product.productGallery)
            : product.productGallery;
        if (Array.isArray(gallery) && gallery.length > 0) {
          arr.push(...gallery);
        }
      } catch {}
    }
    return arr;
  }, [product]);

  const relatedSlides = useMemo(
    () =>
      relatedProducts.filter((p) => p.product_slug !== product?.product_slug),
    [relatedProducts, product],
  );

  // Generate breadcrumbs for schema (must be before early returns)
  const breadcrumbs = useMemo(() => {
    const baseUrl = "https://primecloudsystems.com";
    const crumbs = [
      { name: "Home", url: baseUrl },
      { name: "Shop", url: `${baseUrl}/shop` },
    ];

    if (product?.category_slug && product?.categoryName) {
      crumbs.push({
        name: product.categoryName,
        url: `${baseUrl}/category/${product.category_slug}`,
      });
    }

    if (product?.product_slug && product?.productName) {
      crumbs.push({
        name: product.productName,
        url: `${baseUrl}/product/${product.product_slug}`,
      });
    }

    return crumbs;
  }, [product]);

  if (loading)
    return (
      <div className={classes.centerWrap}>
        <LoaderComp />
      </div>
    );
  if (!product)
    return (
      <div className={classes.notFoundWrapper}>
        <div className={classes.notFoundCard}>
          <h2 className={classes.title}>Product Not Found</h2>
          <p className={classes.message}>
            The product you are looking for does not exist or has been removed.
          </p>
          <button
            className={classes.backButton}
            onClick={() => navigate("/shop")}
          >
            ← Back to Shop
          </button>
        </div>
      </div>
    );

  const mainImage = galleryImages[selectedImage];
  const specs = [
    { label: "Manufacturer Part#", value: product.sku || "-" },
    { label: "Brand", value: product.brandName || "-" },
    { label: "Category", value: product.categoryName || "-" },
    {
      label: "Availability",
      value: product.availability || product.stock_status || "Contact",
    },
    { label: "Condition", value: product.condition || "New" },
  ];

  console.log("Products: ", product);
  return (
    <div className={classes.productDetailWrapper}>
      {/* Auto-generated Schema for SEO */}
      {product && (
        <PageSchema
          type="Product"
          title={product.metaTitle || product.productName}
          description={
            product.metaDescription ||
            product.productShortDesc ||
            `Buy ${product.productName} online.`
          }
          url={`https://primecloudsystems.com/product/${product.product_slug}`}
          product={product}
          breadcrumbs={breadcrumbs}
        />
      )}

      {/* Breadcrumb */}
      <div className={classes.breadcrumbRow}>
        <div className={classes.breadcrumb}>
          <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> /
          {product.category_slug && (
            <Link to={`/category/${product.category_slug}`}>
              {product.categoryName}
            </Link>
          )}{" "}
          /{" "}
          <span>
            {product.productName.length > 70
              ? product.productName.slice(0, 70) + "…"
              : product.productName}
          </span>
        </div>

        <div className={classes.backRow}>
          <button className={classes.backButton} onClick={() => navigate(-1)}>
            ← Back to Shop
          </button>
        </div>
      </div>

      {loading ? (
        <div className={classes.skeletonWrapper}>
          <ProductCardDetailSkeleton />
        </div>
      ) : (
        <>
          {/* Two Columns */}
          <div className={classes.twoColumnRow}>
            {/* Left – Sticky Image Column */}
            <div className={classes.imageColumn}>
              <div className={classes.mainImageWrap}>
                <ProductImageZoom
                  src={getImageUrl(mainImage)}
                  alt={product.productName}
                />
              </div>
              {galleryImages.length > 1 && (
                <div className={classes.thumbsRow}>
                  {galleryImages.map((img, idx) => (
                    <button
                      key={idx}
                      className={`${classes.thumbnail} ${
                        idx === selectedImage ? classes.activeThumb : ""
                      }`}
                      onClick={() => setSelectedImage(idx)}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt="thumbnail"
                        onError={(e) => {
                          if (e?.target && e.target.src !== PLACEHOLDER_IMAGE) {
                            e.target.src = PLACEHOLDER_IMAGE;
                          }
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right – Product Info */}
            <div className={classes.infoColumn}>
              <h1 className={classes.productName}>{product.productName}</h1>

              {product.productShortDesc && (
                <div className={classes.shortDesc}>
                  <p>{product.productShortDesc}</p>
                </div>
              )}

              {/* Specs Table */}
              <div className={classes.specsTable}>
                {specs.map((s, idx) => (
                  <div key={idx} className={classes.specRow}>
                    <div className={classes.specLabel}>{s.label}</div>
                    <div className={classes.specValue}>{s.value}</div>
                  </div>
                ))}
              </div>

              <div className={classes.benefitsBottom}>
                <div className={classes.benefitCard}>
                  <div className={classes.benefitIcon}>
                    <img
                      src="../img/sidebar-icon/free-delivery.svg"
                      alt="free-delivery"
                    />
                  </div>

                  <div className={classes.benefitText}>
                    <h4>Free Delivery</h4>
                  </div>
                </div>

                <div className={classes.benefitCard}>
                  <div className={classes.benefitIcon}>
                    <img src="../img/sidebar-icon/support.svg" alt="support" />
                  </div>

                  <div className={classes.benefitText}>
                    <h4>Support 24/7</h4>
                  </div>
                </div>

                <div className={classes.benefitCard}>
                  <div className={classes.benefitIcon}>
                    <img
                      src="../img/sidebar-icon/return-refund.svg"
                      alt="return-refund"
                    />
                  </div>

                  <div className={classes.benefitText}>
                    <h4>Return & Refund</h4>
                  </div>
                </div>

                <div className={classes.benefitCard}>
                  <div className={classes.benefitIcon}>
                    <img
                      src="../img/sidebar-icon/secure-payment.svg"
                      alt="secure-payment"
                    />
                  </div>

                  <div className={classes.benefitText}>
                    <h4>Secure payment</h4>
                  </div>
                </div>
              </div>

              <div className={classes.actionsRow}>
                <GetAQuoteButton sku={product.sku} text={"Request a Quote"} />
              </div>
            </div>

            {/* Benefits Section */}
            <div className={classes.infoColumn}>
              <div className={classes.expertTeamContainer}>
                <img
                  src={"../img/home-img/expert-team.jpg"}
                  alt="expert team"
                />
                <h1>Expert team support</h1>
                <p className={classes.extraText}>
                  Looking for a large quantity of <span>{product.sku}</span>?
                  Call us at <Link to="tel:+8444211258">844-421-1258</Link> or
                  Request a Quote below and one of our sales representative will
                  get in touch with you very soon.
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className={classes.tabsRow}>
            <div className={classes.tabsHeader}>
              <button className={`${classes.tabBtn} ${classes.activeTab}`}>
                Description
              </button>
            </div>
            <div className={classes.tabContent}>
              {product.productDesc ? (
                <div
                  className={classes.descriptionHtml}
                  dangerouslySetInnerHTML={{ __html: product.productDesc }}
                />
              ) : (
                <p>No description available.</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Related Products */}
      {relatedSlides.length > 0 && (
        <section className={classes.relatedSection}>
          <h2>Related Products</h2>
          {relatedLoading ? (
            <div className={classes.skeletonWrapper}>
              <ProductCardSkeleton count={4} />
            </div>
          ) : (
            <SliderComp slidesToShow={4} arrows dots={false} gap={20}>
              {relatedSlides.map((related) => (
                <div key={related.id} className={classes.relatedCardWrapper}>
                  <ProductCard
                    product={{
                      ...related,
                      productImage: getImageUrl(related.productImage),
                    }}
                  />
                </div>
              ))}
            </SliderComp>
          )}
        </section>
      )}
    </div>
  );
}
